import { useState } from 'react';
import {
    Box,
    Flex,
    HStack,
    VStack,
    Text,
    Icon,
    Divider,
    Checkbox,
} from '@chakra-ui/react';
import { TbArrowBackUp, TbChevronDown, TbGripVertical } from 'react-icons/tb';
import type { ColumnPrefs } from '../../../hooks/useReportingColumnPrefs';
import {
    nestedRowsToPrefs,
    reorderNestedRows,
    type NestedColumnDrawerChild,
    type NestedColumnDrawerRow,
    type NestedColumnGroupDef,
} from '../nestedColumnGroups';

export type SettingsColumnDrawerChild = NestedColumnDrawerChild;

export interface SettingsColumnDrawerRow {
    key: string;
    label: string;
    visible: boolean;
    pinned: boolean;
    children?: SettingsColumnDrawerChild[];
}

export interface SettingsColumnsPanelProps {
    columnRows: SettingsColumnDrawerRow[];
    dragKey: string | null;
    setDragKey: (key: string | null) => void;
    onColumnToggle: (key: string) => void;
    onColumnReorder: (fromKey: string, toKey: string) => void;
    showResetColumns: boolean;
    onResetColumnsClick: () => void;
    pendingResetColumns: boolean;
}

/** Flatten nested drawer rows to leaf prefs. Parent group ids are omitted. */
export function columnDrawerRowsToPrefs(
    rows: SettingsColumnDrawerRow[],
    allKeys?: string[],
    groups: NestedColumnGroupDef[] = [],
): ColumnPrefs {
    const hasNested = rows.some((r) => r.children && r.children.length > 0);
    if (!hasNested) {
        return {
            order: rows.map((r) => r.key),
            hidden: rows.filter((r) => !r.visible && !r.pinned).map((r) => r.key),
        };
    }
    const leafKeys = allKeys ?? rows.flatMap((r) =>
        r.children?.length ? r.children.map((c) => c.key) : [r.key],
    );
    return nestedRowsToPrefs(rows as NestedColumnDrawerRow[], leafKeys, groups);
}

export function reorderColumnRows(
    rows: SettingsColumnDrawerRow[],
    fromKey: string,
    toKey: string,
): SettingsColumnDrawerRow[] {
    const hasNested = rows.some((r) => r.children && r.children.length > 0);
    if (hasNested) {
        return reorderNestedRows(rows as NestedColumnDrawerRow[], fromKey, toKey);
    }
    if (fromKey === toKey) return rows;
    const fromRow = rows.find((r) => r.key === fromKey);
    const toRow = rows.find((r) => r.key === toKey);
    if (!fromRow || !toRow || fromRow.pinned || toRow.pinned) return rows;
    const next = [...rows];
    const fromIdx = next.findIndex((r) => r.key === fromKey);
    if (fromIdx < 0) return rows;
    const [item] = next.splice(fromIdx, 1);
    const newToIdx = next.findIndex((r) => r.key === toKey);
    if (newToIdx < 0) return rows;
    next.splice(newToIdx, 0, item);
    return next;
}

const SettingsColumnsPanel = ({
    columnRows,
    dragKey,
    setDragKey,
    onColumnToggle,
    onColumnReorder,
    showResetColumns,
    onResetColumnsClick,
    pendingResetColumns,
}: SettingsColumnsPanelProps) => {
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
    const hasNested = columnRows.some((r) => r.children && r.children.length > 0);

    return (
        <>
            <Box
                mb={3}
                px={3}
                py={2.5}
                bg="gray.50"
                border="1px solid"
                borderColor="gray.200"
                borderRadius="md"
            >
                <Text fontSize="xs" color="gray.500">
                    {hasNested
                        ? 'Drag to reorder. Expand a field to turn on Total, Avg, or Median as separate columns.'
                        : 'Drag rows to reorder. Uncheck to hide a column.'}
                </Text>
            </Box>
            <VStack spacing={0} align="stretch">
                {columnRows.map((row) => {
                    const hasChildren = Boolean(row.children?.length);
                    const isExpanded = hasChildren && expandedGroups.has(row.key);
                    return (
                        <Box key={row.key}>
                            <Flex
                                align="center"
                                gap={2}
                                py={2}
                                px={2}
                                borderRadius="md"
                                _hover={{ bg: 'gray.50' }}
                                draggable={!row.pinned}
                                onDragStart={(e) => {
                                    if (row.pinned) return;
                                    setDragKey(row.key);
                                    e.dataTransfer.effectAllowed = 'move';
                                    e.dataTransfer.setData('text/plain', row.key);
                                }}
                                onDragEnd={() => setDragKey(null)}
                                onDragOver={(e) => {
                                    e.preventDefault();
                                    e.dataTransfer.dropEffect = 'move';
                                }}
                                onDrop={(e) => {
                                    e.preventDefault();
                                    const from = e.dataTransfer.getData('text/plain') || dragKey;
                                    if (from && from !== row.key) {
                                        onColumnReorder(from, row.key);
                                    }
                                    setDragKey(null);
                                }}
                                opacity={dragKey === row.key ? 0.6 : 1}
                            >
                                <Checkbox
                                    isChecked={row.visible}
                                    isDisabled={row.pinned}
                                    onChange={() => onColumnToggle(row.key)}
                                    flexShrink={0}
                                />
                                <Text fontSize="sm" flex={1} noOfLines={1}>
                                    {row.label}
                                </Text>
                                {hasChildren && (
                                    <Icon
                                        as={TbChevronDown as React.ElementType}
                                        boxSize={4}
                                        color="gray.400"
                                        cursor="pointer"
                                        transform={isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)'}
                                        transition="transform 0.15s"
                                        flexShrink={0}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setExpandedGroups((prev) => {
                                                const next = new Set(prev);
                                                if (next.has(row.key)) next.delete(row.key);
                                                else next.add(row.key);
                                                return next;
                                            });
                                        }}
                                    />
                                )}
                                {!row.pinned && (
                                    <Icon
                                        as={TbGripVertical as React.ElementType}
                                        boxSize={4}
                                        color="gray.400"
                                        cursor="grab"
                                        flexShrink={0}
                                    />
                                )}
                            </Flex>
                            {isExpanded && row.children?.map((child) => (
                                <Flex
                                    key={child.key}
                                    align="center"
                                    gap={2}
                                    py={1.5}
                                    pl={8}
                                    pr={2}
                                    borderRadius="md"
                                    _hover={{ bg: 'gray.50' }}
                                    draggable
                                    onDragStart={(e) => {
                                        setDragKey(child.key);
                                        e.dataTransfer.effectAllowed = 'move';
                                        e.dataTransfer.setData('text/plain', child.key);
                                    }}
                                    onDragEnd={() => setDragKey(null)}
                                    onDragOver={(e) => {
                                        e.preventDefault();
                                        e.dataTransfer.dropEffect = 'move';
                                    }}
                                    onDrop={(e) => {
                                        e.preventDefault();
                                        const from = e.dataTransfer.getData('text/plain') || dragKey;
                                        if (from && from !== child.key) {
                                            onColumnReorder(from, child.key);
                                        }
                                        setDragKey(null);
                                    }}
                                    opacity={dragKey === child.key ? 0.6 : 1}
                                >
                                    <Checkbox
                                        isChecked={child.visible}
                                        onChange={() => onColumnToggle(child.key)}
                                        flexShrink={0}
                                    />
                                    <Text fontSize="sm" flex={1} noOfLines={1}>
                                        {child.label}
                                    </Text>
                                    <Icon
                                        as={TbGripVertical as React.ElementType}
                                        boxSize={4}
                                        color="gray.400"
                                        cursor="grab"
                                        flexShrink={0}
                                    />
                                </Flex>
                            ))}
                        </Box>
                    );
                })}
            </VStack>
            {showResetColumns && (
                <>
                    <Divider my={4} />
                    <Flex
                        align="center"
                        cursor="pointer"
                        onClick={onResetColumnsClick}
                        _hover={{ bg: 'gray.50' }}
                        borderRadius="md"
                        p={2}
                        mx={-2}
                        bg={pendingResetColumns ? 'blue.50' : undefined}
                    >
                        <HStack spacing={3}>
                            <Icon as={TbArrowBackUp as React.ElementType} boxSize={5} color="gray.500" />
                            <Box>
                                <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                                    Reset Columns to Default
                                </Text>
                                <Text fontSize="xs" color="gray.500">
                                    Restore column visibility and order
                                </Text>
                            </Box>
                        </HStack>
                    </Flex>
                </>
            )}
        </>
    );
};

export default SettingsColumnsPanel;
